import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login({ onLogin }) {
  const [step, setStep] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [resending, setResending] = useState(false);
  const [otpHint, setOtpHint] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('expired') === '1') {
      toast.error('Your session has expired. Please sign in again.');
      window.history.replaceState({}, '', '/login');
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const sent = response.data.message || 'Verification code sent';
      toast.success(sent);
      setOtpHint(sent);
      setOtpSent(true);
      setStep('otp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResending(true);
    try {
      const response = await axios.post(`${API}/auth/resend-otp`, { email });
      const sent = response.data.message || 'A new code has been sent';
      toast.success(sent);
      setOtpHint(sent);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not resend the code');
    } finally {
      setResending(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success('If the account exists, a reset code has been sent');
      setOtpHint('');
      setStep('reset');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { email, otp: resetOtp, new_password: newPassword });
      toast.success('Password reset. Please sign in.');
      setStep('login');
      setPassword('');
      setResetOtp('');
      setNewPassword('');
      setDisplayOtp('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, { email, otp });
      toast.success('Login successful');
      onLogin(response.data.access_token, response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4">
      <Card className="w-full max-w-md shadow-lg border-2 border-[#8FC043]" data-testid="login-card">
        <CardHeader className="space-y-4">
          <div className="flex justify-center">
            <img src="/himan-logo.png" alt="Himan Investment & Company" className="h-24 w-24 object-contain" />
          </div>
          <CardTitle className="text-2xl font-bold text-center">Himan Investment & Company</CardTitle>
          <CardDescription className="text-center">
            {step === 'login' && 'Enter your credentials to continue'}
            {step === 'otp' && (otpHint || 'Enter the verification code we sent you')}
            {step === 'forgot' && 'Enter your email — we will text the reset code to the phone on your account'}
            {step === 'reset' && 'Enter the reset code and your new password'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 'login' ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  data-testid="password-input"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button
                data-testid="login-submit-button"
                type="submit"
                className="w-full"
                disabled={loading}
              >
                {loading ? <Loader2 className="animate-spin" /> : 'Continue'}
              </Button>
              <button
                type="button"
                data-testid="forgot-password-link"
                className="w-full text-sm text-gray-500 hover:text-[#8FC043] underline"
                onClick={() => setStep('forgot')}
              >
                Forgot password?
              </button>
            </form>
          ) : step === 'forgot' ? (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email</Label>
                <Input id="forgot-email" data-testid="forgot-email-input" type="email" placeholder="you@company.com"
                  value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <Button data-testid="forgot-submit-button" type="submit" className="w-full" disabled={loading}>
                {loading ? <Loader2 className="animate-spin" /> : 'Send Reset Code'}
              </Button>
              <Button type="button" variant="outline" className="w-full" onClick={() => setStep('login')}>
                Back to Login
              </Button>
            </form>
          ) : step === 'reset' ? (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-otp">Reset Code</Label>
                <Input id="reset-otp" data-testid="reset-otp-input" type="text" placeholder="6-digit code"
                  value={resetOtp} onChange={(e) => setResetOtp(e.target.value)} maxLength={6} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input id="new-password" data-testid="new-password-input" type="password" placeholder="At least 8 characters"
                  value={newPassword} onChange={(e) => setNewPassword(e.target.value)} minLength={8} required />
              </div>
              <Button data-testid="reset-submit-button" type="submit" className="w-full" disabled={loading}>
                {loading ? <Loader2 className="animate-spin" /> : 'Reset Password'}
              </Button>
              <Button type="button" variant="outline" className="w-full" onClick={() => setStep('login')}>
                Back to Login
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="otp">OTP Code</Label>
                <Input
                  id="otp"
                  data-testid="otp-input"
                  type="text"
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength={6}
                  required
                />
              </div>
              <Button
                data-testid="verify-otp-button"
                type="submit"
                className="w-full"
                disabled={loading}
              >
                {loading ? <Loader2 className="animate-spin" /> : 'Verify OTP'}
              </Button>
              <button
                type="button"
                data-testid="resend-otp-link"
                className="w-full text-sm text-gray-500 hover:text-[#8FC043] underline"
                onClick={handleResendOtp}
                disabled={resending}
              >
                {resending ? 'Sending…' : "Didn't get the code? Resend"}
              </button>
              <Button
                data-testid="back-to-login-button"
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => {
                  setStep('login');
                  setOtp('');
                  setOtpSent(false);
                }}
              >
                Back to Login
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
